import { useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { V, runRules, errSummary } from "@/lib/formErrors";
import { TipoSoggetto, SocietaFields, isGiuridica, regoleGiuridica } from "@/components/SoggettoFields";

const SOC_G = [["societa_forma"], ["societa_piva"], ["societa_cf"], ["societa_comune"], ["societa_cap"], ["societa_email"], ["societa_pec"], ["societa_sdi"], ["societa_telefono"]];

const DA_COMPLETARE = ["luogo_nascita", "data_nascita", "codice_fiscale", "res_comune", "res_via", "res_civico", "email"];
const RES = ["res_comune", "res_via", "res_civico"];
export const anagraficaIncompleta = (a) => !!a && DA_COMPLETARE.some((k) => !String(a[k] || "").trim() && !(RES.includes(k) && String(a.residenza || "").trim()));

const CAMPI = [
  ["nome", "Nome *"], ["cognome", "Cognome *"], ["luogo_nascita", "Luogo di nascita"], ["data_nascita", "Data di nascita", "date"],
  ["codice_fiscale", "Codice fiscale"], ["res_comune", "Residenza: comune"], ["res_via", "Residenza: via"], ["res_civico", "Residenza: civico"],
  ["telefono", "Telefono *"], ["telefono2", "Telefono 2"], ["email", "Email"], ["pec", "PEC"],
];
const SOC = [["societa_ragione_sociale", "Ragione sociale"], ["societa_sede", "Sede (comune)"], ["societa_via", "Via"], ["societa_civico", "Civico"], ["societa_cf_piva", "CF / P.IVA"]];
const OBBL = ["nome", "cognome", "telefono"];
const inp = "w-full px-3 py-2 rounded-lg bg-white border outline-none text-sm focus:border-[#FC82ED]";
const lab = "block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1";
const isoData = (v) => { const m = String(v || "").match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/); return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : String(v || "").slice(0, 10); };

function Campo({ k, label, type, f, set, arancio, err }) {
  const vuoto = !String(f[k] || "").trim();
  const req = isGiuridica(f) ? ["nome", "cognome"] : OBBL;
  const lbl = isGiuridica(f) && k === "telefono" ? "Telefono" : label;
  const cls = err ? "border-red-500 bg-red-50" : vuoto && req.includes(k) ? "border-red-300" : vuoto && arancio ? "border-orange-400 bg-orange-50" : "border-[var(--border)]";
  return (
    <div data-field-error={err ? "true" : undefined}><label className={lab}>{lbl}</label>
      <input data-testid={`cli-${k}`} type={type || "text"} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className={`${inp} ${cls}`} />
      {err && <p className="text-[11px] text-red-600 mt-1" data-testid={`cli-err-${k}`}>{err}</p>}</div>
  );
}

export default function AnagraficaCliente({ cliente, immobileId, onClose, onSaved, tipo = "acquirente" }) {
  const [f, setF] = useState(() => ({ in_qualita: "in_proprio", ...(cliente || {}), data_nascita: isoData(cliente?.data_nascita) }));
  const legacyRes = !RES.some((k) => cliente?.[k]) && cliente?.residenza;
  const [busy, setBusy] = useState(false);
  const [doppione, setDoppione] = useState(null);
  const [errs, setErrs] = useState({});
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErrs((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; }); };
  const giur = isGiuridica(f);
  const obbl = giur ? ["nome", "cognome", "societa_ragione_sociale", "societa_piva"] : OBBL;
  const payload = () => Object.fromEntries([...CAMPI, ...SOC, ...SOC_G, ["in_qualita"], ["qualita_di"], ["tipo_soggetto"]].map(([k]) => [k, f[k] || ""]));
  const salva = async () => {
    if (obbl.some((k) => !String(f[k] || "").trim())) return toast.error(giur ? "Ragione sociale, Partita IVA e nome/cognome del legale rappresentante sono obbligatori" : "Nome, cognome e telefono sono obbligatori");
    const e1 = runRules({ codice_fiscale: [V.cf], email: [V.email], pec: [V.email], telefono: [V.phone], telefono2: [V.phone], societa_cf_piva: [V.cf], ...(giur ? regoleGiuridica : {}) }, f);
    setErrs(e1);
    if (Object.keys(e1).length) return toast.error(errSummary(Object.keys(e1).length));
    setBusy(true); setDoppione(null);
    try {
      if (tipo === "venditore") {
        if (!immobileId) { toast.error("Scegli prima l'immobile della vendita"); return; }
        const { data } = await api.post("/proprietari", { ...payload(), immobili_ids: [immobileId] });
        toast.success("Venditore creato e selezionato");
        onSaved(data);
        return;
      }
      const { data: v } = await api.post("/proposte/cliente/verifica", { telefono: f.telefono, codice_fiscale: f.codice_fiscale, societa_piva: giur ? f.societa_piva : undefined, escludi_id: cliente?.id });
      if (v.doppione) { setDoppione(v.doppione); return; }
      const { data } = cliente?.id ? await api.put(`/proposte/cliente/${cliente.id}`, payload()) : await api.post("/proposte/cliente", { ...payload(), immobile_id: immobileId });
      toast.success(cliente?.id ? "Anagrafica aggiornata" : "Acquirente creato e selezionato");
      onSaved(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.doppione) setDoppione(d.doppione); else if (d?.field_errors) setErrs(d.field_errors); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4" data-testid="anagrafica-cliente-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[calc(100dvh-1rem)] flex flex-col card-immo overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[var(--border)]">
          <h3 className="font-display text-lg font-bold">{cliente?.id ? "Completa anagrafica" : "Nuovo cliente"}</h3>
          <button onClick={onClose} data-testid="cli-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 flex-1 overflow-y-auto overscroll-contain space-y-4">
          <TipoSoggetto value={f.tipo_soggetto} onChange={(v) => set("tipo_soggetto", v)} prefix="cli" />
          <p className="text-[11px] text-[var(--muted)]">{giur ? "Obbligatori: ragione sociale, Partita IVA e nome/cognome del legale rappresentante." : "Obbligatori solo Nome, Cognome e Telefono."} I campi in arancione mancano e si possono completare anche dopo.</p>
          {giur && <><h4 className="font-display font-bold text-sm">Dati società</h4><SocietaFields f={f} set={set} errors={errs} prefix="cli" />
            <h4 className="font-display font-bold text-sm">Legale rappresentante (chi firma)</h4>
            <Campo k="qualita_di" label="Qualifica (es. amministratore unico)" f={f} set={set} arancio /></>}
          {legacyRes && <p className="text-xs" data-testid="cli-residenza-attuale">Residenza attuale: <b>{cliente.residenza}</b> (resta invariata se lasci vuoti comune, via e civico)</p>}
          <div className="grid sm:grid-cols-2 gap-3">{CAMPI.map(([k, l, t]) => <Campo key={k} k={k} label={l} type={t} f={f} set={set} arancio={DA_COMPLETARE.includes(k)} err={errs[k]} />)}</div>
          {!giur && <div>
            <label className={lab}>Interviene</label>
            <div className="flex gap-2 flex-wrap">
              {[["in_proprio", "In proprio"], ["in_qualita", "In qualità di…"]].map(([k, l]) => (
                <button key={k} type="button" data-testid={`cli-qualita-${k}`} onClick={() => set("in_qualita", k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${f.in_qualita === k ? "bg-[#111111] text-white border-[#111111]" : "bg-white border-[var(--border)]"}`}>{l}</button>
              ))}
            </div>
          </div>}
          {!giur && f.in_qualita === "in_qualita" && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="cli-societa">
              <Campo k="qualita_di" label="In qualità di (es. legale rappresentante)" f={f} set={set} arancio />
              {SOC.map(([k, l]) => <Campo key={k} k={k} label={l} f={f} set={set} arancio err={errs[k]} />)}
            </div>
          )}
          {doppione && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="cli-doppione">
              <div className="flex items-center gap-2 font-semibold"><AlertTriangle size={15} /> Esiste già un cliente con lo stesso telefono, codice fiscale o Partita IVA</div>
              {doppione.visibile ? (
                <button data-testid="cli-usa-esistente" onClick={() => onSaved({ id: doppione.id, _esistente: true })}
                  className="mt-2 px-3 py-1.5 rounded-lg bg-[#111111] text-white text-xs font-semibold">Usa quello esistente ({doppione.nome})</button>
              ) : <p className="text-xs mt-1">È assegnato a un altro agente: chiedi al direttore di condividerlo.</p>}
            </div>
          )}
        </div>
        <div className="flex gap-2 px-5 py-3 border-t border-[var(--border)] bg-[var(--card)]">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>
          <button onClick={salva} disabled={busy} data-testid="cli-salva" className="flex-1 inline-flex justify-center items-center gap-2 py-2.5 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm disabled:opacity-60">
            {busy && <Loader2 size={15} className="animate-spin" />} {cliente?.id ? "Salva anagrafica" : tipo === "venditore" ? "Salva e seleziona" : "Salva e usa nella proposta"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
